<template>
  <main :class="cn('min-md:max-w-large min-md:justify-center mx-auto mt-16 flex flex-col', props.class)">
    <UiTabs
      v-model:model-value="selectedServiceId"
      :list="staticProducts"
      :variants="{ default: 'ghost', active: 'default' }"
      :swiper="{
        enabled: isMobile,
      }"
      :classes="{
        root: 'gap-2',
      }"
    />

    <div class="mt-4 flex h-full flex-col gap-5">
      <PensionPortfolioValuePropositionTable :structure="selectedCategoryTable?.table" :isLoading />

      <div class="flex flex-col">
        <PensionPortfolioValuePropositionCheckList
          v-if="!props.isManualContinueProcess"
          :items="checklistItems"
        />
        <PensionPortfolioValuePropositionCashbackInfoBanner
          :class="!props.isManualContinueProcess ? 'rounded-b-xl' : 'mb-8 rounded-xl'"
        />
      </div>
    </div>
  </main>
</template>

<script setup lang="tsx">
const { isMobile } = useDisplay()

const props = withDefaults(
  defineProps<{
    class?: ClassValue
    isLocked?: boolean
    isManualContinueProcess?: boolean
    noRecommendationsFlow?: boolean
  }>(),
  { isLocked: true, isManualContinueProcess: false, noRecommendationsFlow: false }
)

const PortfolioProductTypes = {
  Pension: "pension-savings",
  LifeInsurance: "life-insurance",
  disabilityInsurance: "work-disability",
} as const

const staticProducts = [
  {
    value: PortfolioProductTypes.Pension,
    label: "חיסכון פנסיוני",
  },
  {
    value: PortfolioProductTypes.LifeInsurance,
    label: "כיסוי ביטוח חיים",
  },
  {
    value: PortfolioProductTypes.disabilityInsurance,
    label: "אובדן כושר עבודה",
  },
]

const selectedServiceId = ref(staticProducts[0].value)

const { data: tableData, isLoading } = useGetMyValuePropositionTableData(selectedServiceId)

const selectedCategoryTable = computed(() => {
  if (isLoading.value) return skeletonData()

  switch (selectedServiceId.value) {
    case PortfolioProductTypes.Pension:
      return pensionSavingsTable(tableData.value)

    case PortfolioProductTypes.LifeInsurance:
      return lifeInsuranceTable(tableData.value)

    case PortfolioProductTypes.disabilityInsurance:
      return disabilityInsuranceTable(tableData.value)

    default:
      return pensionSavingsTable(tableData.value)
  }
})

const checklistItems = computed(() => {
  if (props.noRecommendationsFlow) {
    return [
      () => (
        <p class="text-sm font-light">
          ריכזנו את כל הפנסיה שלך במקום אחד, עם הסברים פשוטים וברורים. ה-AI שלנו לא&nbsp; מפסיק לנתח את השוק
          ולזהות הזדמנויות לשיפור. כדי לקבל תובנות שיכולות לחסוך לך כסף – כדאי להשלים את ההצטרפות ל־FINQ.
        </p>
      ),
    ]
  }

  return [
    () => (
      <p class="text-sm font-light">
        הגדלת הקצבה החודשית בחיסכון הפנסיוני בעוד&nbsp;
        <span class="font-medium text-violet-600">2,176 ₪</span>
      </p>
    ),
    () => (
      <p class="text-sm font-light">
        הגדלת החיסכון בקרנות ההשתלמות בעוד
        <span class="font-medium text-violet-600"> 50,000 ₪</span>
      </p>
    ),
    () => (
      <p class="text-sm font-light">
        התאמת רמת הסיכון שלך ל<span class="font-medium text-violet-600">מוגברת</span>
      </p>
    ),
    () => (
      <p class="text-sm font-light">
        הוספת ביטוח חיים בסכום של
        <span class="font-medium text-violet-600"> 1,500,000 ₪</span>
      </p>
    ),
    () => (
      <p class="text-sm font-light">
        עדכון הסטטוס המשפחתי תחת&nbsp;
        <span class="font-medium text-violet-600">ביטול הפקדות שארים</span>
      </p>
    ),
  ]
})

const pensionSavingsTable = (data: ValuePropositionTableData | undefined) => {
  if (!data?.pensionSavings) return undefined

  const { lumpSum, monthlyAllowance } = data.pensionSavings

  return {
    table: [
      {
        header: "סכום חד-פעמי",
        icon: "portfolio/coins-stacked",
        values: [
          {
            name: "סכום חד-פעמי שצברת עד היום",
            value: toCash(lumpSum.current, NIS),
            infoPopup: {
              title: "עלות מצטברת עד תום ביטוח",
              description:
                "עלות הביטוחים עולה עם הגיל, ולפיכך העלות המצטברת גדולה יותר ממכפלת העלות החודשית, במספר החודשים שנותרו",
            },
          },
          {
            name: "סכום חד-פעמי שתקבל בפרישה",
            value: toCash(lumpSum.forecast, NIS),
            infoPopup: {
              title: "עלות מצטברת עד תום ביטוח",
              description:
                "עלות הביטוחים עולה עם הגיל, ולפיכך העלות המצטברת גדולה יותר ממכפלת העלות החודשית, במספר החודשים שנותרו",
            },
          },
        ],
      },
      {
        header: "קצבה חודשית",
        icon: "portfolio/coins",
        values: [
          {
            name: "סכום קצבה שצברת עד היום",
            value: toCash(monthlyAllowance.current, NIS),
            infoPopup: {
              title: "עלות מצטברת עד תום ביטוח",
              description:
                "עלות הביטוחים עולה עם הגיל, ולפיכך העלות המצטברת גדולה יותר ממכפלת העלות החודשית, במספר החודשים שנותרו",
            },
          },
          {
            name: "קצבה חודשית שתקבל בפרישה",
            value: toCash(monthlyAllowance.forecast, NIS),
            infoPopup: {
              title: "עלות מצטברת עד תום ביטוח",
              description:
                "עלות הביטוחים עולה עם הגיל, ולפיכך העלות המצטברת גדולה יותר ממכפלת העלות החודשית, במספר החודשים שנותרו",
            },
          },
        ],
      },
    ],
  }
}

const lifeInsuranceTable = (data: ValuePropositionTableData | undefined) => {
  if (!data?.lifeInsurance) return undefined

  const { deathBenefit, survivorsAllowance } = data.lifeInsurance

  return {
    table: [
      {
        header: "ביטוח חיים",
        icon: "portfolio/coins-stacked",
        values: [
          {
            name: "עלות חודשית לביטוח חיים",
            value: toCash(deathBenefit.monthlyAllowance, NIS),
            infoPopup: {
              title: "עלות מצטברת עד תום ביטוח",
              description:
                "עלות הביטוחים עולה עם הגיל, ולפיכך העלות המצטברת גדולה יותר ממכפלת העלות החודשית, במספר החודשים שנותרו",
            },
          },
          {
            name: "סכום ביטוח חיים",
            value: toCash(deathBenefit.lumpSum, NIS),
            infoPopup: {
              title: "עלות מצטברת עד תום ביטוח",
              description:
                "עלות הביטוחים עולה עם הגיל, ולפיכך העלות המצטברת גדולה יותר ממכפלת העלות החודשית, במספר החודשים שנותרו",
            },
          },
        ],
      },
      {
        header: "קצבת שארים",
        icon: "portfolio/coins",
        values: [
          {
            name: "עלות לקצבת שארים",
            value: toCash(survivorsAllowance.monthlyAllowance, NIS),
            infoPopup: {
              title: "עלות מצטברת עד תום ביטוח",
              description:
                "עלות הביטוחים עולה עם הגיל, ולפיכך העלות המצטברת גדולה יותר ממכפלת העלות החודשית, במספר החודשים שנותרו",
            },
          },
          {
            name: "סכום קצבת שארים",
            value: toCash(survivorsAllowance.lumpSum, NIS),
            infoPopup: {
              title: "עלות מצטברת עד תום ביטוח",
              description:
                "עלות הביטוחים עולה עם הגיל, ולפיכך העלות המצטברת גדולה יותר ממכפלת העלות החודשית, במספר החודשים שנותרו",
            },
          },
        ],
      },
    ],
  }
}

const disabilityInsuranceTable = (data: ValuePropositionTableData | undefined) => {
  if (!data?.disabilityInsurance) return undefined

  const { monthlyCost, monthlyAllowance } = data.disabilityInsurance

  return {
    table: [
      {
        header: "אובדן כושר עבודה",
        icon: "portfolio/coins-stacked",
        values: [
          {
            name: "עלות חודשית לאובדן כושר עבודה",
            value: toCash(monthlyCost, NIS),
            infoPopup: {
              title: "עלות מצטברת עד תום ביטוח",
              description:
                "עלות הביטוחים עולה עם הגיל, ולפיכך העלות המצטברת גדולה יותר ממכפלת העלות החודשית, במספר החודשים שנותרו",
            },
          },
          {
            name: "סכום קצבה חודשית",
            value: toCash(monthlyAllowance, NIS),
            infoPopup: {
              title: "עלות מצטברת עד תום ביטוח",
              description:
                "עלות הביטוחים עולה עם הגיל, ולפיכך העלות המצטברת גדולה יותר ממכפלת העלות החודשית, במספר החודשים שנותרו",
            },
          },
        ],
      },
    ],
  }
}

const skeletonData = () => {
  return {
    table: [
      {
        header: "lorem",
        icon: "portfolio/coins-stacked",
        values: [
          { name: "lorem", value: "lorem" },
          { name: "lorem", value: "lorem" },
        ],
      },
      {
        header: "lorem",
        icon: "portfolio/coins",
        values: [
          { name: "lorem", value: "lorem" },
          { name: "lorem", value: "lorem" },
        ],
      },
    ],
  }
}
</script>
